import { Card, CardContent, Typography, Box, SvgIcon, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { AreaChart, Area, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

type LineChartCardProps = {
  title: string;
  data: Array<{ name: string; value: number }>;
  color: string;
  height?: number;
  xAxisLabel?: string;
  yAxisLabel?: string;
  showToolbar?: boolean;
  grid?: boolean;
  strokeWidth?: number;
  areaGradient?: boolean;
};

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  borderRadius: 12,
  boxShadow: '0 4px 20px 0 rgba(0, 0, 0, 0.05)',
  border: '1px solid',
  borderColor: theme.palette.divider,
  transition: 'all 0.3s cubic-bezier(.25,.8,.25,1)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 4,
    background: theme.palette.primary.main,
    opacity: 0,
    transition: 'opacity 0.3s ease',
  },
  '&:hover': {
    transform: 'translateY(-6px)',
    boxShadow: '0 8px 30px 0 rgba(0, 0, 0, 0.12)',
    borderColor: theme.palette.error.main,
    '&::before': {
      opacity: 1,
      background: theme.palette.error.main,
    },
  },
}));

const LineChartCard = ({ 
  title, 
  data, 
  color, 
  height = 300,
  xAxisLabel,
  yAxisLabel,
  showToolbar = false,
  grid = true,
  strokeWidth = 2,
  areaGradient = false
}: LineChartCardProps) => {
  const theme = useTheme();
  const textColor = theme.palette.text.secondary;
  const gridColor = theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)';

  return (
    <StyledCard>
      <CardContent>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          {title}
        </Typography>
        <Box sx={{ 
          height,
          '& .recharts-cartesian-grid-horizontal line': {
            stroke: gridColor,
          },
          '& .recharts-cartesian-grid-vertical line': {
            stroke: gridColor,
          },
          '& .recharts-tooltip-wrapper': {
            borderRadius: '8px',
            boxShadow: '0 4px 20px 0 rgba(0, 0, 0, 0.12)',
            border: 'none',
          },
        }}>
          <ResponsiveContainer width="100%" height="100%">
        {areaGradient ? (
          <AreaChart data={data} margin={{ top: 10, right: 20, bottom: 30, left: 0 }}>
            <defs>
              <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={color} stopOpacity={0.8}/>
                <stop offset="95%" stopColor={color} stopOpacity={0.1}/>
              </linearGradient>
            </defs>
            {grid && <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />}
            <XAxis 
              dataKey="name" 
              tick={{ 
                fill: textColor, 
                fontSize: 12,
                fontFamily: 'inherit',
              }} 
              axisLine={false}
              tickLine={false}
            />
            <YAxis 
              tick={{ fill: textColor, fontSize: 12 }}
              tickLine={{ stroke: gridColor }}
              axisLine={{ stroke: gridColor }}
            />
            <Tooltip 
              contentStyle={{
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${gridColor}`,
                borderRadius: 4,
                padding: '8px 12px',
                fontSize: 14,
                boxShadow: theme.shadows[2],
              }}
              itemStyle={{ color: theme.palette.text.primary }}
              labelStyle={{ color: theme.palette.text.secondary, fontWeight: 500 }}
              formatter={(value: number) => [value, yAxisLabel || 'Value']}
              labelFormatter={(label) => `${xAxisLabel || 'Period'}: ${label}`}
            />
            <Area 
              type="monotone" 
              dataKey="value" 
              stroke={color} 
              strokeWidth={strokeWidth}
              fillOpacity={1}
              fill="url(#colorValue)"
              activeDot={{
                fill: theme.palette.background.paper,
                stroke: color,
                strokeWidth: 2,
                r: 6,
              }}
            />
          </AreaChart>
        ) : (
          <LineChart data={data} margin={{ top: 10, right: 20, bottom: 30, left: 0 }}>
            {grid && <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />}
            <XAxis 
              dataKey="name" 
              tick={{ 
                fill: textColor, 
                fontSize: 12,
                fontFamily: 'inherit',
              }} 
              axisLine={false}
              tickLine={false}
            />
            <YAxis 
              tick={{ fill: textColor, fontSize: 12 }}
              tickLine={{ stroke: gridColor }}
              axisLine={{ stroke: gridColor }}
            />
            <Tooltip 
              contentStyle={{
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${gridColor}`,
                borderRadius: 4,
                padding: '8px 12px',
                fontSize: 14,
                boxShadow: theme.shadows[2],
              }}
              itemStyle={{ color: theme.palette.text.primary }}
              labelStyle={{ color: theme.palette.text.secondary, fontWeight: 500 }}
              formatter={(value: number) => [value, yAxisLabel || 'Value']}
              labelFormatter={(label) => `${xAxisLabel || 'Period'}: ${label}`}
            />
            <Line 
              type="monotone" 
              dataKey="value" 
              stroke={color} 
              strokeWidth={strokeWidth}
              dot={{
                fill: color,
                stroke: theme.palette.background.paper,
                strokeWidth: 2,
                r: 4,
              }}
              activeDot={{
                fill: theme.palette.background.paper,
                stroke: color,
                strokeWidth: 2,
                r: 6,
              }}
            />
          </LineChart>
        )}
      </ResponsiveContainer>
        </Box>
      </CardContent>
    </StyledCard>
  );
};

export default LineChartCard;
